/**
 * trading-style one-click trading panel, docked on the chart.
 *
 *   [ SELL ]  [ − vol + ]  [ BUY ]
 *   [  bid  ]             [  ask  ]
 *
 * Clicking SELL / BUY places a MARKET order instantly (no modal, no SL —
 * the user adds a stop afterward), exactly like the trading server's one-click trading.
 * Live bid/ask come from this component's own tick subscription so the
 * (very large) Chart component doesn't re-render on every tick. Volume is
 * the shared `ui.tradeVolume` (also used by the right-click trade items).
 *
 * Master-only; the server enforces it too. Toggled via the chart context
 * menu / `O` hotkey (`ui.oneClickPanelOpen`).
 */
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setOneClickPanelOpen, setTradeVolume } from "@/state/slices/uiSlice";
import { pushToast } from "@/state/slices/notificationsSlice";
import { api, ApiError } from "@/api/client";
import { useTickStream, type Tick } from "@/hooks/useTickStream";
import styles from "./OneClickTradePanel.module.css";

function decimalsOf(n: number): number {
  const s = String(n);
  const i = s.indexOf(".");
  return i < 0 ? 0 : Math.min(5, s.length - i - 1);
}

/** trading-style price: integer part normal, first two decimals large, the
 *  remaining digit(s) as a superscript. */
function PriceText({ price }: { price: number | null }) {
  if (price == null || !isFinite(price)) return <span>—</span>;
  const d = Math.max(2, decimalsOf(price));
  const s = price.toFixed(d);
  const [intPart, dec = ""] = s.split(".");
  const big = dec.slice(0, 2);
  const sup = dec.slice(2);
  return (
    <span className={styles.price}>
      <span className={styles.priceInt}>{intPart}</span>
      <span className={styles.priceBig}>{big}</span>
      {sup ? <span className={styles.priceSup}>{sup}</span> : null}
    </span>
  );
}

export function OneClickTradePanel({ symbol }: { symbol: string }) {
  const dispatch = useAppDispatch();
  const volume = useAppSelector((s) => s.ui.tradeVolume);
  const connected = useAppSelector((s) => s.trading.connected);
  const [quote, setQuote] = useState<{ bid: number; ask: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // Own tick subscription → latest bid/ask. The pump fans out per
  // subscriber, so this extra stream is cheap and keeps Chart untouched.
  useTickStream(symbol, {
    onSnapshot: (ticks: Tick[]) => {
      const t = ticks[ticks.length - 1];
      if (t) setQuote({ bid: t.bid, ask: t.ask });
    },
    onTicks: (ticks: Tick[]) => {
      const t = ticks[ticks.length - 1];
      if (t) setQuote({ bid: t.bid, ask: t.ask });
    },
  });

  const spread =
    quote != null ? Math.max(0, quote.ask - quote.bid) : null;

  const trade = async (side: "buy" | "sell") => {
    if (busy || !connected) return;
    setBusy(true);
    try {
      const outcome = await api.tradingPlaceOrder({ symbol, side, volume });
      if (outcome.kind === "filled") {
        dispatch(
          pushToast({
            kind: "success",
            title: `${side === "buy" ? "Buy" : "Sell"} ${volume} ${symbol} @ ${outcome.result.price} filled`,
          }),
        );
      } else if (outcome.kind === "rejected") {
        dispatch(
          pushToast({
            kind: "error",
            title: "Order rejected",
            message: outcome.result.human_msg,
          }),
        );
      } else {
        dispatch(
          pushToast({
            kind: "warning",
            title: "Blocked by a risk control",
            message: outcome.message,
          }),
        );
      }
    } catch (e) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Order failed",
          message: e instanceof ApiError ? e.message : "Network error",
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const step = (delta: number) => dispatch(setTradeVolume(volume + delta));

  // The volume field keeps its own text draft so the user can clear it and
  // type freely (backspace to empty, partial "0." etc.) without the Redux
  // number snapping it back to 0 on every keystroke. We commit a valid
  // number to Redux as it's typed, and reconcile to the canonical value on
  // blur. While focused we don't let external volume changes overwrite the
  // raw text (which would reformat mid-edit and jump the cursor).
  const [volDraft, setVolDraft] = useState(String(volume));
  const volFocusedRef = useRef(false);
  useEffect(() => {
    if (!volFocusedRef.current) setVolDraft(String(volume));
  }, [volume]);

  // Drag-to-reposition + Esc-to-close — same convention as the shared
  // SettingsPanel. The panel stays docked in the chart (position: absolute),
  // so we drag in the chart container's coordinate space: capture the panel's
  // offset within its positioned parent (rect - parentRect, transform-safe),
  // then apply pointer deltas. Clamped so it can't be dragged off-screen.
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origTop: number;
    origLeft: number;
    minLeft: number;
    maxLeft: number;
    maxTop: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Esc hides the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setOneClickPanelOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch]);

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    // Let clicks on the close button (or any future control) through.
    if ((e.target as HTMLElement).closest("button, input, select")) return;
    e.preventDefault();
    const el = panelRef.current;
    if (!el) return;
    const parent = el.offsetParent as HTMLElement | null;
    const r = el.getBoundingClientRect();
    const pr = parent?.getBoundingClientRect();
    const parentW = pr?.width ?? window.innerWidth;
    const parentH = pr?.height ?? window.innerHeight;
    const top = r.top - (pr?.top ?? 0);
    const left = r.left - (pr?.left ?? 0);
    const margin = 40;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origTop: top,
      origLeft: left,
      minLeft: margin - r.width,
      maxLeft: parentW - margin,
      maxTop: parentH - margin,
    };
    setPos({ top, left });
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPos({
        top: Math.max(0, Math.min(d.maxTop, d.origTop + (e.clientY - d.startY))),
        left: Math.max(
          d.minLeft,
          Math.min(d.maxLeft, d.origLeft + (e.clientX - d.startX)),
        ),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      onMouseDown={(e) => e.stopPropagation()}
      style={pos ? { top: pos.top, left: pos.left, transform: "none" } : undefined}
    >
      <div
        className={`${styles.head} ${dragging ? styles.headDragging : ""}`}
        onMouseDown={onHeaderMouseDown}
      >
        <span className={styles.title}>One-Click</span>
        <span className={styles.headSymbol}>{symbol}</span>
        <button
          className={styles.closeBtn}
          onClick={() => dispatch(setOneClickPanelOpen(false))}
          title="Hide one-click trading"
          aria-label="Hide one-click trading"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className={styles.row}>
        <button
          className={`${styles.sideBtn} ${styles.sell}`}
          disabled={busy || !connected}
          onClick={() => trade("sell")}
          title="Sell at market"
        >
          SELL
        </button>
        <div className={styles.volWrap}>
          <button className={styles.stepBtn} onClick={() => step(-0.01)}>
            −
          </button>
          <input
            className={styles.volInput}
            type="number"
            min="0.01"
            step="0.01"
            value={volDraft}
            onFocus={() => {
              volFocusedRef.current = true;
            }}
            onChange={(e) => {
              setVolDraft(e.target.value);
              const n = Number(e.target.value);
              if (e.target.value !== "" && isFinite(n) && n > 0)
                dispatch(setTradeVolume(n));
            }}
            onBlur={() => {
              volFocusedRef.current = false;
              const n = Number(volDraft);
              if (!isFinite(n) || n <= 0) {
                setVolDraft(String(volume)); // restore last good value
              } else {
                const clamped = Math.max(0.01, Math.round(n * 100) / 100);
                dispatch(setTradeVolume(clamped));
                setVolDraft(String(clamped));
              }
            }}
          />
          <button className={styles.stepBtn} onClick={() => step(0.01)}>
            +
          </button>
        </div>
        <button
          className={`${styles.sideBtn} ${styles.buy}`}
          disabled={busy || !connected}
          onClick={() => trade("buy")}
          title="Buy at market"
        >
          BUY
        </button>
      </div>
      <div className={styles.quoteRow}>
        <div className={`${styles.quoteBox} ${styles.sellQuote}`}>
          <PriceText price={quote?.bid ?? null} />
        </div>
        <div className={styles.spread}>
          {spread != null ? spread.toFixed(decimalsOf(quote!.bid)) : ""}
        </div>
        <div className={`${styles.quoteBox} ${styles.buyQuote}`}>
          <PriceText price={quote?.ask ?? null} />
        </div>
      </div>
      {!connected ? (
        <div className={styles.offline}>Trading server not connected</div>
      ) : null}
    </div>
  );
}
